import {css, StyleSheet} from 'aphrodite';
import gql from 'graphql-tag';
import React, {useState} from 'react';

import {assert, assertAndContinue} from 'Asserts';
import {Label} from 'bulk-workflows/BulkWorkflowComponents';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import FormGroup from 'ui-library/bs/FormGroup';
import {ButtonStyle} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import {MinimalEntity} from 'ui-library/entity-input/EntityInputConstants';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import Modal from 'ui-library/modals/Modal';

import {
  ReviewerReassignmentQuery,
  ReviewerReassignmentQueryVariables,
} from './__generated/ReviewerReassignmentQuery';
import {EsignatureStatement} from './EsignatureStatement';
import {ReviewStageReviewerAssignmentRow} from './ReviewStageReviewerAssignmentRow';

export const REVIEWER_REASSIGNMENT_QUERY = gql`
  query ReviewerReassignmentQuery($rlcStageId: StrictString!) @skipAudit @engComponent(name: NOTEBOOK) {
    reviewLifecycleStage(id: $rlcStageId) {
      ... on ReviewLifecycleStageNode {
        id
        name
        description
        reviewerType
        selectedTeam {
          id
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface ReviewerReassignmentModalProps {
  reviewLifecycleStageId: string;
  onRequestClose: () => void;
  onRequestSubmit: (result: {selectedReviewerId: string}) => Promise<void>;
  authors: Array<MinimalEntity> | undefined;
  reviewSenderId: string | undefined;
  reviewableId: string;
}

export function ReviewerReassignmentModal({
  reviewLifecycleStageId,
  onRequestClose,
  onRequestSubmit,
  authors,
  reviewSenderId,
  reviewableId,
}: ReviewerReassignmentModalProps): JSX.Element | null {
  const [selectedReviewer, setSelectedReviewer] = useState<MinimalEntity | null>(null);
  const [submitInProgress, setSubmitInProgress] = useState<boolean>(false);
  const [canSubmit, setCanSubmit] = useState<boolean>(submitInProgress);

  const result = useBenchlingQuery<ReviewerReassignmentQuery, ReviewerReassignmentQueryVariables>(
    REVIEWER_REASSIGNMENT_QUERY,
    {
      variables: {rlcStageId: reviewLifecycleStageId},
      fetchPolicy: 'network-only',
    }
  );

  if (result.type === QueryResultType.PENDING) {
    return <HorizontalThrobber />;
  } else if (result.type === QueryResultType.ERROR) {
    return null;
  }

  const {reviewLifecycleStage} = result.data;
  if (isInaccessible(reviewLifecycleStage)) {
    assertAndContinue(
      false,
      'User did not have access to the review lifecycle stage while fetching review data'
    );
    return null;
  }

  const {name, description, reviewerType, selectedTeam} = reviewLifecycleStage;
  const selectedTeamId = selectedTeam ? selectedTeam.id : undefined;

  return (
    <Modal
      closeable={true}
      title="Reassign reviewer"
      showFooterBorder={false}
      onRequestClose={onRequestClose}
      onRequestSubmit={async () => {
        setSubmitInProgress(true);
        try {
          assert(selectedReviewer != null, 'Should not be able to submit without selecting a reviewer');
          await onRequestSubmit({selectedReviewerId: selectedReviewer.id});
        } finally {
          setSubmitInProgress(false);
        }
      }}
      buttons={[
        {
          text: 'Reassign reviewer',
          submit: true,
          style: ButtonStyle.SECONDARY,
          disabled: submitInProgress || !canSubmit,
        },
      ]}
    >
      <FormGroup className={css(styles.formGroup)}>
        <Label className={css(styles.sectionTitle, styles.label)}>{name}</Label>
        <p>{description}</p>
      </FormGroup>
      <FormGroup className={css(styles.formGroup)}>
        <Label className={css(styles.sectionTitle, styles.label)}>Reviewer</Label>
        <ReviewStageReviewerAssignmentRow
          reviewerType={reviewerType}
          authors={authors}
          onChange={(user: MinimalEntity | null) => {
            setSelectedReviewer(user);
            setCanSubmit(user != null);
          }}
          selectedUser={selectedReviewer}
          selectedTeamId={selectedTeamId}
          dataTestAttrs={{
            component: 'ReviewStageReviewerAssignmentRow',
            element: 'root',
            key: reviewLifecycleStage.name,
          }}
          reviewSenderId={reviewSenderId}
          reviewableId={reviewableId}
        />
      </FormGroup>
      <EsignatureStatement action={null} buttonText="Reassign reviewer" />
    </Modal>
  );
}

const styles = StyleSheet.create({
  sectionTitle: {
    color: Colors.NEAR_BLACK,
    fontSize: 15,
  },
  label: {
    marginBottom: 15,
  },
  formGroup: {
    paddingBottom: '8px',
  },
});
